import { getFileInfoFromUri, events, api } from 'src/helpers';
import errorDescriptions from 'src/constants/errors';

// Pure helper functions shared by the type-specific preview components.
// Ported from the old class component's instance methods.
// Kept as plain functions (not hooks) so they can also be called from a loop,
// e.g. when building the media list for the slide viewer.

const MAX_SIZE_BYTES = 20 * 1024 * 1024;

// A file whose status is 'expired' can no longer be viewed/downloaded from
// its original source, so it's always shown as a generic "other" file
// regardless of its real mimetype (image/video/audio/dcm).
export function isExpired(file) {
  return file?.status === 'expired';
}

// One-off (non-reactive) check of whether a file's deleteAt timestamp has
// already passed. `now` defaults to the current time but can be passed in
// for a consistent snapshot across multiple checks in the same tick.
export function isDeleteAtPassed(file, now = Date.now()) {
  return file?.deleteAt != null && now >= file.deleteAt;
}

// Combines both expiry signals — used anywhere we just need a yes/no answer
// once (e.g. filtering the slide-viewer media list), as opposed to the live,
// re-rendering countdown the UI shows (see useCountdown in hooks.js).
export function isFileUnavailable(file, now = Date.now()) {
  return isExpired(file) || isDeleteAtPassed(file, now);
}

// Formats a millisecond duration as a short countdown label:
// under 1h -> "12:05", under 24h -> "3h 12m", 24h+ -> "2d 4h".
export function formatCountdown(ms) {
  if (ms == null || ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// Determine the file type: image / video / audio / dcm / other
export function getFileBoxType(file) {
  if (!file) return null;

  const mimeType = file.mimetype || file.type;
  let type = mimeType ? mimeType.toString().split('/')[0] : null;

  if (type === 'application') {
    type = getFileInfoFromUri(file?.name || file?.originalName)?.extension;
  }
  if (type === 'image' && file.size > MAX_SIZE_BYTES && !file.thumbnail) {
    type = 'other';
  }
  if (file.isDicom) {
    type = 'dcm';
  } else if ((type === 'dcm' || type === 'dicom') && !file.isDicom) {
    type = 'file';
  }
  return type;
}

// Web-only: builds an <img> src. React Native would instead read
// file.uri directly, or use a native asset picker result — no createObjectURL there.
export function loadImg(file) {
  const type = getFileBoxType(file);
  if (type === 'image' && file?.uri) {
    // use a smaller preview variant when available
    return file.thumbnail || file.uri;
  }
  if (file?.uri) return file.uri;
  return URL.createObjectURL(file?.file || file);
}

export function getVideoUri(file) {
  if (!file) return null;
  if (file?.bunnyVideoId && file.bunnyStatus >= 3) {
    return `https://vz-2daa1ecc-4f5.b-cdn.net/${file.bunnyVideoId}/playlist.m3u8`;
  }
  if (file.uri) return file.uri;
  return URL.createObjectURL(file.file || file);
}

export function getVideoThumbnail(file) {
  return file?.thumbnail || null;
}

// Computes a height that preserves the file's aspect ratio for a given width,
// used when the box has no fixed height set.
export function getImageStyle(file, width, height) {
  const fw = file?.width;
  const fh = file?.height;
  if (!fw || !fh || fw <= 0 || fh <= 0) return { width, height };

  const numericWidth = typeof width === 'number' ? width : parseFloat(width);
  if (!numericWidth || Number.isNaN(numericWidth)) return { width, height };

  const hasFixedHeight = typeof height === 'number' && height > 0;
  if (hasFixedHeight) return { width, height };

  const computedHeight = Math.round(numericWidth * (fh / fw));
  return { width, height: computedHeight };
}

// Opens the fullscreen slide viewer for an image/video file, showing every
// image/video in `list` (or just `file` alone when no list is given) and
// selecting `file` as the starting slide.
// Web-only: relies on a global `events` emitter wired up to a web-only
// slide-viewer UI — porting to RN means swapping this for a native modal/screen.
export function openSlideMedia({ file, list, onViewEdit, enableEvent }) {
  const now = Date.now();
  const media = (list || [file])
    .map((item) => item.file || item)
    .filter(
      (item) => ['image', 'video'].includes(getFileBoxType(item)) && !isFileUnavailable(item, now)
    );

  const selectedIndex = media.findIndex(
    (item) =>
      item === file ||
      (file.counter && item.counter && item.counter === file.counter) ||
      (file.fileProcessId && item.fileProcessId && item.fileProcessId === file.fileProcessId)
  );

  events.emit('openSlideMedia', {
    media: media.map((item) => ({
      ...item,
      file: item instanceof File ? item : null,
      type: item.type,
      mimetype: item.mimetype,
      uri: getFileBoxType(item) === 'video' ? getVideoUri(item) : loadImg(item)
    })),
    selectedIndex,
    onViewEdit,
    onSlide: enableEvent
      ? (file) => {
          if (!enableEvent || file?.selfDestruct?.type !== 'first_view') return;
          if (file.deleteAt || file.status === 'expired') return;
          onFirstView(file).then();
        }
      : undefined
  });
}

export const onFirstView = (file) => {
  return new Promise((resolve, reject) => {
    if (file.fileProcessId)
      api('message/firstView', {
        fileProcessId: file.fileProcessId
      }).then(({ error, data }) => {
        if (!error) {
          resolve(data);
        } else {
          reject({ error: errorDescriptions(error) });
        }
      });
    else reject(null);
  });
};
